import React, { useEffect, useState } from "react"
import { Alert, Button, Modal } from "react-bootstrap"
import axios from "axios"
import { handleErrorFetchingData } from "../../../../../functions/errorHandling"
import LoadingAnimation from "../../../../../reuseable-components/LoadingAnimation"
import AssessmentForm from "./AssessmentForm"
import { v4 as uuidv4 } from "uuid"
import swal from 'sweetalert'
import getServerURL from "../../../../../functions/getServerURL"


export const ALL_OF_THE_ABOVE = "All of the above"

export default function EditAssessmentModal({ training, getTrainingDetails }) {
  // STATE
  const [show, toggleShow] = useState(false)
  const [fetchingAssessment, setFetchingAssessment] = useState(false)
  const [assessmentInfo, setAssessmentInfo] = useState({ questions: [], required_score: 0 })
  const [formErrors, setFormErrors] = useState({})
  const [errorMessage, setErrorMessage] = useState("")

  const getAssessment = () => {
    setErrorMessage("")
    if (training.assessment) {
      setFetchingAssessment(true)
      axios.get(`${training.assessment}get_assessment_manager_details/`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
        params: { training: training.id }
      }).then(response => {

        // PUTTING THE ABOVE ALL OPTION TO THE END OF THE LIST OF OPTIONS
        response.data.questions.forEach(question => {
          if (question.type === "Radio"){
            let aboveAll = null
            question.options.forEach((option, index) => {
              if (option.name === ALL_OF_THE_ABOVE) {
                aboveAll = question.options.splice(index, 1)[0]
              }
            })
            if (aboveAll) {
              question.options.push(aboveAll)
            }
          }
        })

        setAssessmentInfo(response.data)
      }).catch(error => {
        handleErrorFetchingData(error)
      }).finally(() => setFetchingAssessment(false))
    } else {
      // UUID doesn't get assigned when calling useState for assessmentInfo,
      // so it's being done here... (reason unknown)
      setAssessmentInfo({ ...assessmentInfo, id: uuidv4() })
      // setErrorMessage("This training has no assessment")
    }
  }

  const editAssessment = (key, value) => {
    setAssessmentInfo({ ...assessmentInfo, [key]: value })
  }

  const addQuestion = (type) => {
    let question = { id: uuidv4(), name: "", type,  options: [], is_new: true }
    question.options = [
      { id: uuidv4(), name: type === "Boolean" ? "True" : "", is_correct: false, is_new: true },
      { id: uuidv4(), name: type === "Boolean" ? "False" : "", is_correct: false, is_new: true },
    ]
    if (type !== "Boolean") {
      question.options = [
        ...question.options,
        { id: uuidv4(), name: "", is_correct: false, is_new: true },
        { id: uuidv4(), name: "", is_correct: false, is_new: true },
      ]
    }
    setAssessmentInfo({ ...assessmentInfo, questions: [ ...assessmentInfo.questions, question ] })
  }

  const editQuestion = (questionId, key, value) => {
    let questions = [...assessmentInfo.questions].map(question => {
      if (question.id === questionId) {
        return { ...question, [key]: value }
      }
      return question
    })

    setAssessmentInfo({ ...assessmentInfo, questions })
  }

  const removeQuestion = (questionId) => {
    let questions = [...assessmentInfo.questions].filter(question => question.id !== questionId)
    setAssessmentInfo({...assessmentInfo, questions })
  }

  const addOption = (questionId, allOfTheAbove = false) => {
    let questions = [...assessmentInfo.questions]
    let question = questions.find(question => question.id === questionId)
    const allOfTheAboveIndex = question.options.findIndex(option => option.name === ALL_OF_THE_ABOVE)

    if (!!allOfTheAbove) {
      question.options.push({ id: uuidv4(), name: ALL_OF_THE_ABOVE, is_correct: false, is_new: true })
    } else {
      const newOption = { id: uuidv4(), name: "", is_correct: false, is_new: true }
      // MAKING SURE THAT THE ALL_OF_THE_ABOVE OPTION IS ALLWAYS AT THE BOTTOM
      if (allOfTheAboveIndex >= 0) {
        question.options.splice(allOfTheAboveIndex, 1, newOption)
        question.options.push({ id: uuidv4(), name: ALL_OF_THE_ABOVE, is_correct: false, is_new: true })
      } else {
        question.options.push(newOption)
      }
    }
    setAssessmentInfo({ ...assessmentInfo, questions })
  }

  const editOption = (questionId, optionId, key, value) => {
    let questions = [...assessmentInfo.questions]
    let question = questions.find(question => question.id === questionId)
    if (key === "is_correct" && question.type !== "Multiselect") {
      question.options = question.options.map(option => {
        if (option.id === optionId) return { ...option, is_correct: true }
        return { ...option, is_correct: false }
      })
    } else {
      let option = question.options.find(option => option.id === optionId)
      option[key] = value
    }
    setAssessmentInfo({ ...assessmentInfo, questions })
  }

  const removeOption = (questionId, optionId) => {
    let questions = [...assessmentInfo.questions]
    let question = questions.find(question => question.id === questionId)
    question.options = question.options.filter(option => option.id !== optionId)
    setAssessmentInfo({ ...assessmentInfo, questions })
  }

  const handleSubmit = () => {
    let assessmentObject = { ...assessmentInfo, training: training.id, name: training.name + " Knowledge Assessment" }
    swal({
      text: `Editing the assessment... Please stand by.`,
      icon: require("../../../../../static/images/spinner.gif"),
      button: false
    })
    axios.post(`${getServerURL()}assessments/assessment_creation/`, assessmentObject, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
    }).then(response => {
      swal({
        title: "Success",
        text: `Assessment has been edited`,
        icon: "success",
        button: "Continue",
      }).then(() => {
        getTrainingDetails()
        setFormErrors({})
      })
    }).catch(error => {
      if (error.response && error.response.data) {
        if (error.response.data.message === "The form contains errors") {
          setFormErrors(error.response.data.errors)
          swal.close()
        } else {
          swal({
            title: "Error",
            text: "There was an error with submitting changes...",
            icon: "error",
            button: "Continue",
          })
        }
      }
    })
  }

  useEffect(() => {
    if (show) {
      getAssessment()
    } else {
      setAssessmentInfo({ name: "", required_score: 0, questions: [] })
      setFormErrors({})
      setErrorMessage("")
    }
  }, [show])

  return (
    <React.Fragment>
      <Button onClick={() => toggleShow(!show)}>{ training.assessment ? "Edit" : "Create" } Assessment</Button>
      <Modal show={ show } bsSize="lg">
        <Modal.Header closeButton onHide={() => toggleShow(false)}>
          <Modal.Title>
            { training.assessment ? "Edit" : "Create" } Assessment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="assessment-form">
          { fetchingAssessment ?
            <LoadingAnimation />
            :
            <div>
              { errorMessage && <Alert bsStyle="danger">{ errorMessage }</Alert> }
              <AssessmentForm
                assessmentInfo={ assessmentInfo }
                editAssessment={ editAssessment }
                addQuestion={ addQuestion }
                editQuestion={ editQuestion }
                removeQuestion={ removeQuestion }
                addOption={ addOption }
                editOption={ editOption }
                removeOption={ removeOption }
                formErrors={ formErrors }
              />
            </div>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => toggleShow(false)}>Cancel</Button>
          <Button disabled={ !assessmentInfo.questions.length } bsStyle="primary" onClick={ handleSubmit }>Submit</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}


// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/TrainingContainer/EditAssessmentModal/index.js